<template>
  <v-container fluid class="ul-container flex-grow-1">

    <!-- Loading -->
    <v-row
      v-if="$store.usersStore.state.loadingUserList"
      align="center"
      justify="center"
      style="height: 100%;"
      no-gutters
    >
      <div class="ul-loading">
        <v-progress-circular :size="44" :width="4" color="#2D4B95" indeterminate />
        <p class="ul-loading-text mb-0">Cargando usuarios…</p>
      </div>
    </v-row>

    <!-- Grid de usuarios -->
    <v-row v-else>
      <v-col cols="12" class="py-1">
				<div class="ul-search-box" :class="{ 'ul-search-box--active': searchQuery }">
					<v-icon size="15" color="#8097C4">mdi-magnify</v-icon>
					<input
						v-model="searchQuery"
						class="ul-search-input"
						placeholder="Buscar por nombre o correo…"
					/>
					<v-icon
						v-if="searchQuery"
						size="15"
						color="#AABBD8"
						style="cursor:pointer"
						@click="searchQuery = ''"
					>mdi-close</v-icon>
				</div>
			</v-col>

      <v-col
        v-for="(user, index) in filteredUsers"
        :key="index"
        cols="12" sm="6" md="4" lg="3" xl="2"
				class="pa-2"
      >
        <div class="ul-card" :style="{ animationDelay: `${index * 40}ms` }">

          <!-- Franja superior con avatar -->
          <div class="ul-card-top">
            <div class="ul-avatar-wrap">
              <img
                v-if="user.info && user.info.avatar"
                :src="user.info.avatar"
                class="ul-avatar"
              />
              <div v-else class="ul-avatar-fallback">
                <v-icon color="white" size="26">mdi-account-outline</v-icon>
              </div>
            </div>
          </div>

          <!-- Cuerpo -->
          <div class="ul-card-body">
            <p class="ul-name">{{ user.name }}</p>
            <span class="ul-job">{{ user.projectjob.userjobtype.name }}</span>

            <div class="ul-info-list mt-3">
              <div class="ul-info-row">
                <v-icon size="13" color="#8097C4">mdi-email-outline</v-icon>
                <span class="ul-info-text">{{ user.email }}</span>
              </div>
              <div class="ul-info-row">
                <v-icon size="13" color="#8097C4">mdi-calendar-range</v-icon>
                <span class="ul-info-text">{{ user.created_at | toDate }}</span>
              </div>
            </div>

            <!-- Acciones -->
            <div
              v-if="checkPermissions(['edit_users']) || canEditProjectPermissions() || canEditCompanyPermissions()"
              class="ul-actions"
            >

              <!-- Fila 1: Editar + Borrar -->
              <div v-if="checkPermissions(['edit_users'])" class="ul-actions-row">
                <v-btn
                  depressed rounded x-small dark
                  color="#E6A23D"
                  class="ul-btn"
                  @click="editUser(user)"
                >
                  <v-icon size="13" class="mr-1">mdi-account-edit-outline</v-icon>
                  Editar
                </v-btn>
                <v-btn
                  depressed rounded x-small dark
                  color="#DD5050"
                  class="ul-btn"
                  @click="removeProjectUser(user.id)"
                >
                  <v-icon size="13" class="mr-1">mdi-trash-can-outline</v-icon>
                  Borrar
                </v-btn>
              </div>

              <!-- Fila 2: Proyectos + Permisos -->
              <div v-if="canEditProjectPermissions() || canEditCompanyPermissions()" class="ul-actions-row">
                <v-btn
                  v-if="checkCompanyLevelPermissions(['set_user_projects'])"
                  depressed rounded x-small dark
                  color="#2D4B95"
                  class="ul-btn"
                  @click="editUserProjects(user)"
                >
                  <v-icon size="13" class="mr-1">mdi-briefcase-outline</v-icon>
                  Proyectos
                </v-btn>

                <!-- Permisos: cada opción respeta su nivel de autorización -->
                <v-menu offset-y v-if="canEditCompanyPermissions()">
                  <template #activator="{ on, attrs }">
                    <v-btn
                      depressed rounded x-small dark
                      color="#1C3A7A"
                      class="ul-btn"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon size="13" class="mr-1">mdi-shield-account-outline</v-icon>
                      Permisos
                      <v-icon size="13" class="ml-1">mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="ul-menu-list">
                    <v-list-item dense @click="editPermisosEmpresaUser(user)">
                      <v-icon size="15" color="#2D4B95" class="mr-2">mdi-domain</v-icon>
                      <v-list-item-title class="ul-menu-item">Permisos Empresa</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="canEditProjectPermissions()" dense @click="editPermisosUser(user)">
                      <v-icon size="15" color="#2D4B95" class="mr-2">mdi-briefcase-account-outline</v-icon>
                      <v-list-item-title class="ul-menu-item">Permisos Proyecto</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-btn
                  v-else-if="canEditProjectPermissions()"
                  depressed rounded x-small dark
                  color="#1C3A7A"
                  class="ul-btn"
                  @click="editPermisosUser(user)"
                >
                  <v-icon size="13" class="mr-1">mdi-shield-account-outline</v-icon>
                  Permisos
                </v-btn>
              </div>

            </div>
          </div>

        </div>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import { removeUserFromProject } from '@/helpers/api/user'
import { checkCompanyLevelPermissions, checkPermissions } from '@/helpers/permissions'

export default {
  name: 'UsersList',
  computed: {
    selectedProject () {
      return this.$store.projectsStore.state.selectedProject
    },
    userInfo () {
      return this.$store.userStore.state.userInfo
    },
    filteredUsers () {
      const q = (this.searchQuery || '').toString().trim().toLowerCase()
      const list = this.$store.usersStore.state.usersList || []
      if (!q) return list
      return list.filter(user => {
        const name = (user.name || '').toString().toLowerCase()
        const email = (user.email || '').toString().toLowerCase()
        return name.includes(q) || email.includes(q)
      })
    },
  },
  data: () => ({
    loading: false,
    searchQuery: '',
  }),
  watch: {
    selectedProject (project, previousProject) {
      if (project && project.id !== previousProject?.id) {
        this.getUsersList()
      }
    },
  },
  mounted () {
    this.getUsersList()
  },
  methods: {
    checkCompanyLevelPermissions,
    checkPermissions,
    checkIfAdmin () {
      return this.selectedProject?.job_type?.id == 1 || this.selectedProject?.job_type?.id == 8
    },
    canEditCompanyPermissions () {
      return this.checkIfAdmin() || this.checkCompanyLevelPermissions(['set_user_permissions'])
    },
    canEditProjectPermissions () {
      return this.canEditCompanyPermissions() || this.checkPermissions(['set_user_permissions'])
    },
    async getUsersList () {
      if (!this.selectedProject) return

      const data = {
        project_id: this.selectedProject.id,
        start: 0,
        limit: 20,
      }
      this.$store.usersStore.dispatch('getUsersList', data)
    },
    async removeProjectUser (user_id) {
      const data = { project_id: this.selectedProject.id, user_id }
      const user = await removeUserFromProject(data)
      if (user.code == 200) {
        this.$store.globalStore.dispatch('addMessage', {
          message: 'El usuario ha sido eliminado del proyecto.',
          title: 'Usuario eliminado',
          created: new Date(),
          type: 'success',
          icon: 'mdi-check-circle',
          color: '#3EBFA3',
        })
        this.getUsersList()
      }
    },
    editUser (user) {
      this.$store.usersStore.commit('setSelectedUserInfo', user)
      this.$store.usersStore.commit('setEditUserModal', true)
    },
    editUserProjects (user) {
      this.$store.usersStore.commit('setSelectedUserInfo', user)
      this.$store.usersStore.commit('setEditUserProjectsModal', true)
    },
    editPermisosUser (user) {
      this.$store.usersStore.commit('setSelectedUserInfo', user)
      this.$store.usersStore.commit('setEditPermisosUserModal', true)
    },
    editPermisosEmpresaUser (user) {
      this.$store.usersStore.commit('setSelectedUserInfo', user)
      this.$store.usersStore.commit('setEditPermisosEmpresaUserModal', true)
    },
  },
}
</script>

<style scoped>
/* ── Contenedor ───────────────────────────────────── */
.ul-container { padding: 20px; }

/* ── Loading ──────────────────────────────────────── */
.ul-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.ul-loading-text { font-size: 13px; color: #888; }

/* ── Tarjeta ──────────────────────────────────────── */
.ul-card {
  border-radius: 14px;
  border: 1px solid #E0E6F5;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: box-shadow 0.2s, transform 0.2s;
  animation: ul-fade-in 0.35s ease both;
}
.ul-card:hover {
  box-shadow: 0 6px 24px rgba(45, 75, 149, 0.12);
  transform: translateY(-2px);
}

@keyframes ul-fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Franja superior ──────────────────────────────── */
.ul-card-top {
  background: linear-gradient(135deg, #1C3A7A 0%, #2D4B95 100%);
  height: 56px;
  position: relative;
  display: flex;
  justify-content: center;
}

/* ── Avatar ───────────────────────────────────────── */
.ul-avatar-wrap {
  position: absolute;
  bottom: -24px;
}
.ul-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  object-fit: cover;
  display: block;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.ul-avatar-fallback {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  background: #8097C4;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

/* ── Cuerpo ───────────────────────────────────────── */
.ul-card-body {
  padding: 34px 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.ul-name {
  font-size: 14px;
  font-weight: 600;
  color: #1C3A7A;
  text-align: center;
  margin-bottom: 4px;
  line-height: 1.3;
}

.ul-job {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: #2D4B95;
  background: #EEF2FB;
  border: 1px solid #C5D0EA;
  border-radius: 20px;
  padding: 2px 10px;
  text-align: center;
}

/* ── Info rows ────────────────────────────────────── */
.ul-info-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.ul-info-row {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
}
.ul-info-text {
  font-size: 11px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Acciones ─────────────────────────────────────── */
.ul-actions {
  width: 100%;
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-top: 1px solid #F0F4FB;
  padding-top: 12px;
}
.ul-actions-row {
  display: flex;
  gap: 6px;
  justify-content: center;
}
.ul-btn {
  font-size: 11px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 26px !important;
  padding: 0 10px !important;
  flex: 1;
}

/* ── Menú desplegable ─────────────────────────────── */
.ul-menu-list { padding: 4px 0; }
.ul-menu-item { font-size: 13px !important; }
.ul-search-box {
  display: flex;
  align-items: center;
  gap: 8px;
	margin-left: auto;
  background: #ffffff;
  border: 1px solid #D0D9EF;
  border-radius: 24px;
  padding: 6px 14px;
  max-width: 320px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.ul-search-box--active,
.ul-search-box:focus-within {
  border-color: #2D4B95;
  box-shadow: 0 0 0 3px rgba(45, 75, 149, 0.08);
}
.ul-search-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 12.5px;
  color: #1C3A7A;
  width: 100%;
}
.ul-search-input::placeholder { color: #6b80ab; }
</style>